import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Users, Database, Brain } from "lucide-react";
import Typewriter from "../components/Typewriter";
import InOutBioBrand from "../components/InOutBioBrand";

export default function Home() {
    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    const stagger = {
        visible: { transition: { staggerChildren: 0.2 } }
    };

    return (
        <>
            {/* Hero Section - Clean Light Background with Rich Gradients */}
            <section className="relative overflow-hidden min-h-[90vh] flex items-center bg-gradient-to-br from-slate-50 via-white to-blue-50">
                {/* Subtle background pattern & blobs */}
                <div className="absolute inset-0 opacity-40 overflow-hidden pointer-events-none">
                    <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-[100px] animate-pulse"></div>
                    <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-purple-100/50 rounded-full blur-[120px]"></div>
                    <div className="absolute top-[40%] left-[20%] w-[300px] h-[300px] bg-indigo-50/50 rounded-full blur-[80px]"></div>
                </div>

                <div className="max-w-7xl mx-auto px-6 w-full relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center py-20">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={stagger}
                        className="text-left"
                    >
                        <motion.div variants={fadeInUp} className="inline-block mb-6">
                            <span className="px-5 py-2.5 bg-white/80 backdrop-blur-sm border border-indigo-100 rounded-full text-indigo-600 text-sm font-bold shadow-sm flex items-center gap-2 w-fit">
                                <Sparkles size={16} className="text-indigo-500" />
                                AI & Data Analytics Consulting
                            </span>
                        </motion.div>

                        <motion.h1 variants={fadeInUp} className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.15] mb-8 tracking-tight">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-indigo-600 to-purple-600 text-3xl md:text-5xl lg:text-6xl block min-h-[200px] lg:min-h-[300px]">
                                <Typewriter
                                    phrases={[
                                        "Aiming for safer and more effective medicines for patients through AI.",
                                        "Advancing novel chemical design through AI.",
                                        "Raising the benchmark for AI-assisted drug discovery.",
                                        "AI molecular design, scaled for CROs and Pharma."
                                    ]}
                                    typeSpeed={50}
                                    deleteSpeed={30}
                                    pauseTime={2500}
                                />
                            </span>
                        </motion.h1>

                        <motion.p variants={fadeInUp} className="text-xl text-slate-600 max-w-xl leading-relaxed mb-10 font-light">
                            We are an AI and data consulting startup working at the intersection of chemical data and drug discovery, enabling research teams to adopt AI responsibly and effectively.
                        </motion.p>

                        <motion.div variants={fadeInUp} className="flex flex-wrap gap-5">
                            <Link to="/services" className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2 text-base">
                                View Solutions
                                <ArrowRight size={19} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link to="/contact" className="px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-xl font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all duration-300 shadow-sm text-base">
                                Hire AI Advisors
                            </Link>
                        </motion.div>
                    </motion.div>

                    {/* Hero Visual - Glassmorphism Card */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="relative block mt-12 lg:mt-0"
                    >
                        <div className="absolute top-10 right-10 w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl -z-10 transform rotate-3"></div>
                        <div className="relative bg-white/70 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/50 overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

                            {/* Abstract decorative elements representing data/AI */}
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="bg-blue-50/80 p-5 rounded-2xl border border-blue-100 flex flex-col items-center text-center">
                                    <Database className="text-blue-600 mb-2" size={32} />
                                    <span className="text-xs font-bold text-blue-800 uppercase tracking-wide">Data Ingestion</span>
                                    <div className="h-1 w-12 bg-blue-200 rounded mt-2"></div>
                                </div>
                                <div className="bg-purple-50/80 p-5 rounded-2xl border border-purple-100 flex flex-col items-center text-center">
                                    <Brain className="text-purple-600 mb-2" size={32} />
                                    <span className="text-xs font-bold text-purple-800 uppercase tracking-wide">AI Analysis</span>
                                    <div className="h-1 w-12 bg-purple-200 rounded mt-2"></div>
                                </div>
                            </div>

                            <img src="/images/compound_lab.png" alt="AI & Chemistry" className="w-full h-64 object-cover rounded-xl shadow-inner mb-6" />

                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">SmartGen Platform</h3>
                                    <p className="text-slate-500 text-sm">Target-Specific Compound Generation</p>
                                </div>
                                <div className="flex gap-1">
                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                    <div className="w-2 h-2 rounded-full bg-green-500 delay-75 animate-pulse"></div>
                                    <div className="w-2 h-2 rounded-full bg-green-500 delay-150 animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section >

            {/* Services Preview - Staggered Cards */}
            < section className="py-24 bg-slate-50" >
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <span className="text-indigo-600 font-semibold tracking-wide uppercase text-sm">Our Expertise</span>
                        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-2 mb-6">Innovative Consulting & Solutions</h2>
                        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                            Bridging the gap between chemical expertise and advanced artificial intelligence.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Card 1: AI Consulting */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-slate-100"
                        >
                            <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 mb-6">
                                <Users size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-3">AI Consulting</h3>
                            <p className="text-slate-600 leading-relaxed mb-4">
                                Strategic advisory for integrating AI into your R&D pipeline. We help you build the roadmap, team, and infrastructure for digital transformation.
                            </p>
                            <Link to="/services" className="text-indigo-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                                Learn more <ArrowRight size={16} />
                            </Link>
                        </motion.div>

                        {/* Card 2: SmartGen Product */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-slate-100"
                        >
                            <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-6">
                                <Sparkles size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-3">SmartGen Product</h3>
                            <p className="text-slate-600 leading-relaxed mb-4">
                                AI pipeline for Generating novel chemical compound specific for the therapeutic target of interest.
                            </p>
                            <Link to="/products" className="text-purple-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                                View Product <ArrowRight size={16} />
                            </Link>
                        </motion.div>

                        {/* Card 3: Chemical Data Management */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-slate-100"
                        >
                            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6">
                                <Database size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-3">Chemical Data Management</h3>
                            <p className="text-slate-600 leading-relaxed mb-4">
                                Robust systems for cleaning, standardizing, and managing chemical compound data to ensure it is AI-ready.
                            </p>
                            <Link to="/services" className="text-blue-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                                Learn more <ArrowRight size={16} />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section >

            {/* InOutBio - External Innovation Platform */}
            <motion.section
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                className="relative overflow-hidden bg-[#0d211c] text-[#fdfdf9]"
            >
                <div className="absolute inset-0 lg:left-1/2">
                    <motion.img
                        initial={{ scale: 1.04 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        src="/images/inoutbio-lab.png"
                        alt="Scientist reviewing a microplate in a research laboratory"
                        className="h-full w-full object-cover opacity-35 lg:opacity-90"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0d211c] via-[#0d211c]/90 to-[#0d211c]/15"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 lg:py-28">
                    <div className="max-w-2xl">
                        <InOutBioBrand className="text-[#fdfdf9] text-2xl mb-10 hover:opacity-80 transition-opacity" />

                        <p className="text-[#d99a36] text-xs font-bold tracking-[0.24em] uppercase mb-5">
                            The home of external innovations
                        </p>
                        <h2 className="font-['InOutBioPoppins'] font-bold text-5xl md:text-6xl leading-[1.03] tracking-[-0.045em] mb-7">
                            Where science finds its way forward<span className="text-[#d99a36]">.</span>
                        </h2>
                        <p className="text-lg md:text-xl leading-relaxed text-[#fdfdf9]/70 max-w-xl mb-9">
                            A reviewed global network where research assets become visible, credible, and ready for the right partnering conversation.
                        </p>

                        <div className="flex flex-wrap gap-4 mb-12">
                            <a
                                href="https://inoutbio.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center gap-3 bg-[#d99a36] text-[#0d211c] px-7 py-4 font-bold hover:bg-[#e4ad51] transition-colors"
                            >
                                Explore InOutBio
                                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </a>
                            <Link
                                to="/products#inoutbio"
                                className="inline-flex items-center gap-3 border border-[#fdfdf9]/30 px-7 py-4 font-semibold hover:border-[#fdfdf9]/70 transition-colors"
                            >
                                View capabilities <ArrowRight size={18} />
                            </Link>
                        </div>

                        <div className="flex flex-wrap gap-x-7 gap-y-3 text-[11px] uppercase tracking-[0.16em] text-[#fdfdf9]/55">
                            <span><span className="text-[#d99a36] mr-2">✓</span>Free submission</span>
                            <span><span className="text-[#d99a36] mr-2">✓</span>Reviewed publication</span>
                            <span><span className="text-[#d99a36] mr-2">✓</span>Controlled engagement</span>
                        </div>
                    </div>
                </div>
            </motion.section>
        </>
    );
}

